/**
 * Safe localStorage wrapper that handles errors, availability checks, and provides fallbacks
 */

interface StorageResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class SafeStorage {
  private isAvailable: boolean | null = null;

  /**
   * Check if localStorage is available and working
   */
  private checkAvailability(): boolean {
    if (this.isAvailable !== null) {
      return this.isAvailable;
    }

    try {
      const testKey = '__storage_test__';
      const testValue = 'test';
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      
      this.isAvailable = retrieved === testValue;
      return this.isAvailable;
    } catch (error) {
      console.warn('localStorage is not available:', error);
      this.isAvailable = false;
      return false;
    }
  }

  /**
   * Safely get an item from localStorage
   */
  getItem(key: string): StorageResult<string> {
    if (!this.checkAvailability()) {
      return {
        success: false,
        error: 'localStorage not available'
      };
    }

    try {
      const value = localStorage.getItem(key);
      return {
        success: true,
        data: value ?? undefined
      };
    } catch (error) {
      console.error(`Error getting localStorage item "${key}":`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Safely set an item in localStorage
   */
  setItem(key: string, value: string): StorageResult<void> {
    if (!this.checkAvailability()) {
      return {
        success: false,
        error: 'localStorage not available'
      };
    }

    try {
      localStorage.setItem(key, value);
      return { success: true };
    } catch (error) {
      console.error(`Error setting localStorage item "${key}":`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Safely remove an item from localStorage
   */
  removeItem(key: string): StorageResult<void> {
    if (!this.checkAvailability()) {
      return {
        success: false,
        error: 'localStorage not available'
      };
    }

    try {
      localStorage.removeItem(key);
      return { success: true };
    } catch (error) {
      console.error(`Error removing localStorage item "${key}":`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Safely get and parse JSON from localStorage
   */
  getJSON<T>(key: string, defaultValue?: T): StorageResult<T> {
    const result = this.getItem(key);
    
    if (!result.success || result.data === null || result.data === undefined) {
      return {
        success: false,
        data: defaultValue,
        error: result.error || 'No data found'
      };
    }

    try {
      const parsed = JSON.parse(result.data);
      return {
        success: true,
        data: parsed
      };
    } catch (error) {
      console.error(`Error parsing JSON from localStorage "${key}":`, error);
      return {
        success: false,
        data: defaultValue,
        error: 'Invalid JSON data'
      };
    }
  }

  /**
   * Safely stringify and store JSON in localStorage
   */
  setJSON<T>(key: string, value: T): StorageResult<void> {
    try {
      const serialized = JSON.stringify(value);
      return this.setItem(key, serialized);
    } catch (error) {
      console.error(`Error stringifying data for localStorage "${key}":`, error);
      return {
        success: false,
        error: 'Failed to serialize data'
      };
    }
  }

  /**
   * Get a string value with a fallback
   */
  getString(key: string, defaultValue: string = ''): string {
    const result = this.getItem(key);
    return result.success && result.data !== undefined && result.data !== null ? result.data : defaultValue;
  }

  /**
   * Get a boolean value with a fallback
   */
  getBoolean(key: string, defaultValue: boolean = false): boolean {
    const result = this.getItem(key);
    if (!result.success || result.data === null || result.data === undefined) {
      return defaultValue;
    }
    
    // Handle common boolean representations
    const value = result.data.toLowerCase();
    if (value === 'true') return true;
    if (value === 'false') return false;
    
    return defaultValue;
  }

  /**
   * Set a boolean value
   */
  setBoolean(key: string, value: boolean): StorageResult<void> {
    return this.setItem(key, String(value));
  }

  /**
   * Clear all items (use with caution)
   */
  clear(): StorageResult<void> {
    if (!this.checkAvailability()) {
      return {
        success: false,
        error: 'localStorage not available'
      };
    }

    try {
      localStorage.clear();
      return { success: true };
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if localStorage is currently available
   */
  isStorageAvailable(): boolean {
    return this.checkAvailability();
  }
}

// Export a singleton instance
export const safeStorage = new SafeStorage();

// Export the type for consumers
export type { StorageResult };